import type { CSSProperties } from "react";
import { NavElementKey, ThemeConfig, ThemeNavConfig, ThemeTokenKey } from "@/lib/types";

// Фон/рамка/тень попапов меню (профиль, уведомления). Прозрачность — как у
// «главного меню» (сайдбара): тот же матовый var(--nav-side-bg) + blur(12px).
// Плюс тонкий стеклянный блик по кромке и мягкая тень для «пилюльного» вида.
// Инлайн (а не CSS-класс) — чтобы гарантированно подхватывалось HMR при правках.
export function glassPillStyle(dark: boolean): CSSProperties {
  return {
    // Прозрачность в духе сайдбара (var(--nav-side-bg)), но темнее: попап стоит
    // прямо над колонкой навигации, а вложенный backdrop-filter НЕ размывает
    // соседние пункты (ограничение браузера) — поэтому текст сзади гасим
    // непрозрачностью (примешиваем чёрный). Светлую тему не трогаем.
    background: dark
      ? "color-mix(in srgb, #000 80%, var(--nav-side-bg, color-mix(in srgb, var(--surface) 70%, transparent)))"
      : "color-mix(in srgb, #fff 80%, var(--nav-side-bg, color-mix(in srgb, var(--surface) 70%, transparent)))",
    backdropFilter: "blur(80px) saturate(1.3)",
    WebkitBackdropFilter: "blur(80px) saturate(1.3)",
    border: dark ? "1px solid rgba(255,255,255,0.09)" : "1px solid rgba(124,58,237,0.16)",
    boxShadow: dark
      ? "inset 0 1px 0 rgba(255,255,255,0.09), 0 16px 40px rgba(0,0,0,0.60)"
      : "inset 0 1px 0 rgba(255,255,255,0.95), 0 14px 36px rgba(124,58,237,0.14)",
  };
}

export const themeTokenLabels: Record<ThemeTokenKey, string> = {
  bg: "Основной фон",
  surface: "Поверхность",
  surfaceElevated: "Поднятая карточка",
  border: "Границы",
  text: "Основной текст",
  textMuted: "Вторичный текст",
  primary: "Primary",
  primaryText: "Текст на primary",
  secondary: "Secondary",
  secondaryText: "Текст на secondary",
  accent: "Accent",
  accentText: "Текст на accent",
  success: "Успех",
  warning: "Предупреждение",
  danger: "Ошибка",
  scrollThumb: "Скроллбар",
};

export const themeTokenOrder = Object.keys(themeTokenLabels) as ThemeTokenKey[];

// ── Матовые панели навигации ────────────────────────────────────────────────
export const navElementLabels: Record<NavElementKey, string> = {
  top: "Верхняя панель вкладок",
  side: "Левый сайдбар",
  aside: "Правая панель отдела",
};
export const navElementOrder = Object.keys(navElementLabels) as NavElementKey[];

// Дефолт повторяет прежние захардкоженные значения матовых полос: верхняя —
// на основном фоне (bg) 68%, боковые панели — на поверхности (surface) 70/68%.
// ⚠️ РАДИУС ПАНЕЛЕЙ ПРИБИТ ЧИСЛОМ, а не унаследован от темы. Без него фолбэк
// `--nav-*-radius` = var(--radius-lg), то есть ОБЩИЙ радиус темы, и любая
// правка скругления в конструкторе тянет за собой бары: у рельса срезает углы,
// и он «едет» вместе со страницей (правка Максима 26.08.2026, дважды). Бары —
// самостоятельный элемент каркаса, их скругление меняется отдельным полем в
// «Конструкторе темы» (updateNavRadius), а не заодно с карточками и полями.
export const defaultNavConfig: ThemeNavConfig = {
  top: { radius: 18, light: { color: "#f3f2f8", opacity: 68 }, dark: { color: "#000000", opacity: 68 } },
  // ⚠️ Тёмный сайдбар сведён с макетом (31.08.2026): в образце колонка навигации
  // ровно в тон панелям (17,18,23 при фоне 10,11,16), а не темнее их. Прежние
  // #101010 при 32% давали 11,12,15 — колонка читалась отдельным чёрным полем.
  side: { radius: 18, light: { color: "#ffffff", opacity: 32 }, dark: { color: "#17181d", opacity: 55 } },
  aside: { radius: 18, light: { color: "#ffffff", opacity: 32 }, dark: { color: "#101010", opacity: 32 } },
};

// CSS-переменная матовой панели навигации из цвета+непрозрачности.
export function navBgVar(style: { color: string; opacity: number }): string {
  return `color-mix(in srgb, ${style.color} ${style.opacity}%, transparent)`;
}

// Фолбэк скругления, если radius не задан: side (парящая панель) = общий радиус
// темы, верх/право — 0. Значение — валидная CSS-строка для `--nav-<el>-radius`.
export const navRadiusFallback: Record<NavElementKey, string> = {
  top: "var(--radius-lg)",
  side: "var(--radius-lg)",
  aside: "var(--radius-lg)",
};
export function navRadiusVar(radius: number | undefined | null, el: NavElementKey): string {
  return radius == null ? navRadiusFallback[el] : `${radius}px`;
}

export const defaultThemeConfig: ThemeConfig = {
  light: {
    bg: "#f3f2f8",
    surface: "#ffffff",
    surfaceElevated: "#faf9ff",
    border: "#e0ddf0",
    text: "#16113a",
    textMuted: "#6b6490",
    primary: "#7c3aed",
    primaryText: "#ffffff",
    secondary: "#ede9ff",
    secondaryText: "#5b21b6",
    accent: "#06b6d4",
    accentText: "#ffffff",
    success: "#059669",
    warning: "#d97706",
    danger: "#dc2626",
    scrollThumb: "#c5cfe0",
  },
  dark: {
    bg: "#000000",
    surface: "#101010",
    surfaceElevated: "#181818",
    border: "#323232",
    text: "#f2f2f2",
    textMuted: "#8c8c8c",
    primary: "#7c5cff",
    primaryText: "#ffffff",
    secondary: "#181818",
    secondaryText: "#c4b5fd",
    accent: "#22d3ee",
    accentText: "#0a0a0a",
    success: "#34d399",
    warning: "#fbbf24",
    danger: "#fb7185",
    scrollThumb: "#3c3c3c",
  },
  // Общий радиус портала: панели и крупные карточки. Производный «средний»
  // радиус (поля, кнопки, меню, чипы) считается из него как max(8, radius − 6),
  // см. applyTheme в components/theme-provider.tsx.
  // 18 → 12 → 8 (Максим, 26.08.2026): референс PLUX даёт ~10 px, но на плотных
  // рабочих таблицах строже смотрится 8 — вёрстка перестаёт быть «пузырчатой».
  // Элементы при этом получают 6 (см. формулу выше).
  radius: 8,
  baseFontSize: 15,
  nav: defaultNavConfig,
};

export const MODE_STORAGE_KEY = "company-portal-mode";
export const TOKEN_STORAGE_KEY = "company-portal-token";
// Кэш применённой темы (палитры light/dark + radius + baseFontSize) — читается
// инлайн-скриптом в layout.tsx ДО первой отрисовки, чтобы не было флэша
// дефолтных цветов, пока грузится JS и тема с бэкенда.
export const THEME_CACHE_KEY = "company-portal-theme-cache";
